import { useEffect } from 'react';

interface DocumentTitleOptions {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  noIndex?: boolean;
}

export function useDocumentTitle({ 
  title, 
  description, 
  keywords, 
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterTitle,
  twitterDescription,
  twitterImage,
  noIndex = false
}: DocumentTitleOptions = {}) {
  
  // Set document title
  useEffect(() => {
    if (title) {
      document.title = title;
    }
  }, [title]);

  // Set meta description
  useEffect(() => {
    if (description) {
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', description);
    }
  }, [description]);

  // Set meta keywords
  useEffect(() => {
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords);
    }
  }, [keywords]);

  // Set canonical URL
  useEffect(() => {
    if (canonicalUrl) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonicalUrl);
    }
  }, [canonicalUrl]);

  // Set robots meta tag
  useEffect(() => {
    let robotsTag = document.querySelector('meta[name="robots"]');
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.setAttribute('name', 'robots');
      document.head.appendChild(robotsTag);
    }
    robotsTag.setAttribute('content', noIndex ? 'noindex, nofollow' : 'index, follow');
  }, [noIndex]);

  // Set Open Graph title
  useEffect(() => {
    const ogTitleContent = ogTitle || title;
    if (ogTitleContent) {
      let ogTitleTag = document.querySelector('meta[property="og:title"]');
      if (!ogTitleTag) {
        ogTitleTag = document.createElement('meta');
        ogTitleTag.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitleTag);
      }
      ogTitleTag.setAttribute('content', ogTitleContent);
    }
  }, [ogTitle, title]);

  // Set Open Graph description
  useEffect(() => {
    const ogDescContent = ogDescription || description;
    if (ogDescContent) {
      let ogDescTag = document.querySelector('meta[property="og:description"]');
      if (!ogDescTag) {
        ogDescTag = document.createElement('meta');
        ogDescTag.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescTag);
      }
      ogDescTag.setAttribute('content', ogDescContent);
    }
  }, [ogDescription, description]);

  // Set Open Graph image
  useEffect(() => {
    if (ogImage) {
      let ogImageTag = document.querySelector('meta[property="og:image"]');
      if (!ogImageTag) {
        ogImageTag = document.createElement('meta');
        ogImageTag.setAttribute('property', 'og:image');
        document.head.appendChild(ogImageTag);
      }
      ogImageTag.setAttribute('content', ogImage);
    }
  }, [ogImage]);

  // Set Open Graph type
  useEffect(() => {
    if (ogType) {
      let ogTypeTag = document.querySelector('meta[property="og:type"]');
      if (!ogTypeTag) {
        ogTypeTag = document.createElement('meta');
        ogTypeTag.setAttribute('property', 'og:type');
        document.head.appendChild(ogTypeTag);
      }
      ogTypeTag.setAttribute('content', ogType);
    }
  }, [ogType]);

  // Set Open Graph URL
  useEffect(() => {
    const currentUrl = canonicalUrl || window.location.href;
    let ogUrlTag = document.querySelector('meta[property="og:url"]');
    if (!ogUrlTag) {
      ogUrlTag = document.createElement('meta');
      ogUrlTag.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrlTag);
    }
    ogUrlTag.setAttribute('content', currentUrl);
  }, [canonicalUrl]);

  // Set Twitter title
  useEffect(() => {
    const twitterTitleContent = twitterTitle || ogTitle || title;
    if (twitterTitleContent) {
      let twitterTitleTag = document.querySelector('meta[property="twitter:title"]');
      if (!twitterTitleTag) {
        twitterTitleTag = document.createElement('meta');
        twitterTitleTag.setAttribute('property', 'twitter:title');
        document.head.appendChild(twitterTitleTag);
      }
      twitterTitleTag.setAttribute('content', twitterTitleContent);
    }
  }, [twitterTitle, ogTitle, title]);

  // Set Twitter description
  useEffect(() => {
    const twitterDescContent = twitterDescription || ogDescription || description;
    if (twitterDescContent) {
      let twitterDescTag = document.querySelector('meta[property="twitter:description"]');
      if (!twitterDescTag) {
        twitterDescTag = document.createElement('meta');
        twitterDescTag.setAttribute('property', 'twitter:description');
        document.head.appendChild(twitterDescTag);
      }
      twitterDescTag.setAttribute('content', twitterDescContent);
    }
  }, [twitterDescription, ogDescription, description]);

  // Set Twitter image
  useEffect(() => {
    const twitterImageContent = twitterImage || ogImage;
    if (twitterImageContent) {
      let twitterImageTag = document.querySelector('meta[property="twitter:image"]');
      if (!twitterImageTag) {
        twitterImageTag = document.createElement('meta');
        twitterImageTag.setAttribute('property', 'twitter:image');
        document.head.appendChild(twitterImageTag);
      }
      twitterImageTag.setAttribute('content', twitterImageContent);
    }
  }, [twitterImage, ogImage]);
} 